import React from 'react';
import { Sankey, Tooltip, ResponsiveContainer } from 'recharts';
import { Info } from 'lucide-react';

// Each node gets a semantic colour by status so the flow is scannable at a
// glance: positive outcomes green, rejections red, neutral/pending slate, etc.
const NODE_COLORS = {
  'Ergebnisse': '#0ea5e9',    // sky — entry point
  'Kontaktiert': '#6366f1',   // indigo — in-process hub
  'In Kontakt': '#a78bfa',    // light violet — contacted, nothing scheduled yet
  'Interview': '#8b5cf6',     // violet — in conversation
  'Probestunde': '#4338ca',   // deep indigo — trial shift
  'Zusage': '#10b981',        // emerald — positive outcome
  'Absage': '#ef4444',        // red — rejection
  'Zurückgezogen': '#f59e0b', // amber — withdrawn
  'Unbearbeitet': '#94a3b8'   // slate — untouched
};
const nodeColor = (name) => NODE_COLORS[name] || '#0ea5e9';

// recharts <Sankey> draws only the node rectangles — no labels. This custom
// node renders the bar plus the node's name and count above it (above avoids
// overlapping the horizontal flows, which matters for the middle column).
function SankeyNode({ x, y, width, height, payload, colors }) {
  const name = payload?.name ?? '';
  const value = payload?.value;
  const cx = x + width / 2;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={nodeColor(name)} fillOpacity={0.9} rx={1} />
      <text x={cx} y={y - 5} textAnchor="middle" fontSize={11} fill={colors.value}>
        <tspan fontWeight="600" fill={colors.name}>{name}</tspan>
        {typeof value === 'number' && <tspan dx={5}>{value}</tspan>}
      </text>
    </g>
  );
}

export function BewerbungsflussSankey({ flow }) {
  if (!flow || flow.total === 0 || flow.links.length === 0) {
    return <div className="text-sm text-slate-400 py-8 text-center">Noch keine Ergebnisse im Zeitraum.</div>;
  }
  // SVG text can't use Tailwind `dark:` variants, so pick label colors from the
  // active theme (.dark on <html>) for readable contrast in both modes.
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const colors = isDark
    ? { name: '#e2e8f0', value: '#94a3b8' }
    : { name: '#334155', value: '#64748b' };
  const notice = isDark
    ? { bg: 'rgba(245, 158, 11, 0.12)', border: '#f59e0b', text: '#fcd34d' }
    : { bg: '#fffbeb', border: '#f59e0b', text: '#92400e' };
  return (
    <div>
      <ResponsiveContainer width="100%" height={300}>
        <Sankey
          data={{ nodes: flow.nodes, links: flow.links }}
          nodePadding={26}
          link={{ stroke: '#94a3b8' }}
          node={<SankeyNode colors={colors} />}
          margin={{ top: 22, right: 40, bottom: 8, left: 40 }}
        >
          <Tooltip />
        </Sankey>
      </ResponsiveContainer>
      <div
        role="note"
        style={{
          display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.75rem',
          padding: '0.55rem 0.7rem', borderRadius: '0.5rem',
          borderLeft: `3px solid ${notice.border}`, background: notice.bg
        }}
      >
        <Info size={14} style={{ color: notice.border, flexShrink: 0, marginTop: '1px' }} aria-hidden="true" />
        <p style={{ fontSize: '11px', lineHeight: 1.45, color: notice.text, margin: 0 }}>
          Stufen sind aus dem aktuellen Status abgeleitet (kein Verlaufsprotokoll).
          {' '}{flow.untouchedPct}% der Ergebnisse sind noch „Unbearbeitet" — der Fluss ist entsprechend unsicher.
        </p>
      </div>
    </div>
  );
}
